import { gql } from '$lib/apollo';

export const PRODUCT_EVENT_FIELDS = gql`
  fragment ProductEventFields on API_ProductEvent {
    id
    completedAt
    createdAt
    scheduledAt
    liveAt
    seatLimit
    seatsLeft
    state
    cancelationReason
    canceledAt
    durationSeconds
    supporterAccess
    extendedCount
    extensionsLeft
    extendedDurationSeconds
    purchasedProduct {
      id
      state
      name
      addons {
        id
        name
        price
        description
      }
      event {
        id
        muxAsset {
          id
          playbackIds {
            id
          }
          storyboardSigningKey
          signingKey
          status
          assetId
        }
        token
        recordings {
          id
          playbackIds {
            id
          }
          thumbnailUrl
          storyboardSigningKey
          signingKey
          status
          assetId
          durationSeconds
        }
      }
    }
    account {
      id
      slug
      name
      bio
      summary
      isOnline
      metafyPartner
      productCoachingCount
      languages
      sessionPriceValue
      productGuides {
        totalCount
      }
      avatar {
        original {
          url
        }
      }
      cover {
        original {
          url
        }
      }
      ownedGroup {
        id
        slug
        title
        description
        visibility
        groupTiers {
          id
          price {
            value
          }
          setupPrice {
            value
          }
          hasSetupFee
        }
        membership {
          id
          account {
            id
          }
          groupTier {
            id
          }
        }
        membership {
          isSupporter
          id
          pendingTier {
            id
            name
          }
          account {
            id
            slug
          }
          groupTier {
            id
            name
            description
            cover {
              original {
                url
              }
            }
            group {
              id
              slug
            }
            price {
              currency
              subunits
              value
            }
            pricingOption {
              id
            }
            benefits {
              id
              icon
              discountPercent
              benefitType
              title
              context
            }
          }
        }
        product {
          id
        }
        memberCount
        postCount
        logo {
          original {
            url
          }
        }
        cover {
          original {
            url
          }
        }
        game {
          id
          slug
          logoMain {
            original {
              url
            }
          }
          localizedTitles {
            en
          }
        }
      }
    }
    game {
      id
      slug
      localizedShortTitles {
        en
      }
      localizedTitles {
        en
      }
      logoMain {
        original {
          url
        }
      }
      communityArtwork {
        id
        artwork {
          original {
            url
          }
        }
      }
      artwork {
        original {
          url
        }
      }
    }
    group {
      id
      slug
      title
      description
      visibility
      membership {
        id
        account {
          id
        }
      }
      memberCount
      postCount
      logo {
        original {
          url
        }
      }
      cover {
        original {
          url
        }
      }
      game {
        id
        slug
        logoMain {
          original {
            url
          }
        }
        localizedTitles {
          en
        }
      }
    }
    language
    learningOutcomes
    offerReplayPurchase
    replayPrice {
      id
      price
    }
    chatRoom {
      id
      closedAt
    }
    product {
      id
      slug
      name
      description
      enabled
      totalRevenue
      addonsEnabled
      groupTierBenefits(first: 10) {
        edges {
          node {
            id
            benefitType
            discountPercent
            groupTier {
              id
              name
              pricingOption {
                id
                price
              }
              group {
                id
                title
              }
            }
          }
        }
      }
      visibilities {
        edges {
          node {
            id
            group {
              id
            }
          }
        }
      }
      account {
        id
        slug
        metafyPartner
        groupTeamMemberships(states: [ACCEPTED]) {
          edges {
            node {
              id
              percentageSplit
              account {
                id
                slug
              }
              group {
                id
                slug
                title
                logo {
                  original {
                    url
                  }
                }
                account {
                  id
                  slug
                }
              }
            }
          }
        }
      }
      orderItemForAccount {
        id
        subTotal
        discounts
        itemDiscounts {
          id
          discountAmount
          discountType
          discountedTotal
          originalTotal
        }
      }
      cover {
        original {
          url
        }
      }
      addons {
        id
        name
        price
        description
      }
      unifiedTags {
        edges {
          node {
            id
            name
            slug
          }
        }
      }
      groupDiscountAvailable {
        discountPercent
        group {
          id
          title
        }
      }
      pricingOptions {
        id
        price
        supporterOnly
      }
      accountsRecentlyPurchased(first: 10) {
        edges {
          node {
            id
            slug
            name
            avatar {
              original {
                url
              }
            }
            metafyPartner
            metafyPartnerType
            cover {
              original {
                url
              }
            }
          }
        }
      }
      hasLeftTestimonial
    }
  }
`;

export const GET_PRODUCT_EVENT = gql`
  query GetProductEvent($id: UUID, $slug: String) {
    productEvent(id: $id, slug: $slug) {
      ...ProductEventFields
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_PRODUCT_EVENTS = gql`
  query GetProductEvents(
    $first: Int
    $after: String
    $sort: [ProductEventSortOption]
    $languages: [String!]
    $completed: ProductEventCompletedEnum
  ) {
    productEvents(first: $first, after: $after, sort: $sort, languages: $languages, completed: $completed) {
      edges {
        node {
          ...ProductEventFields
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_LANGUAGES = gql`
  query GetLanguages {
    gameFilter {
      session {
        languages {
          name
        }
      }
    }
  }
`;

export const GET_PRODUCT_EVENTS_PER_GAME = gql`
  query GetProductEventsPerGame(
    $slug: String
    $id: UUID
    $first: Int
    $after: String
    $sort: [ProductEventSortOption]
    $states: [ProductEventStateEnum!]
    $languages: [String!]
  ) {
    game(slug: $slug, id: $id) {
      id
      slug
      productEvents(first: $first, after: $after, sort: $sort, states: $states, languages: $languages) {
        edges {
          node {
            ...ProductEventFields
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCount
      }
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_ACCOUNT_PRODUCT_EVENTS = gql`
  query GetAccountEvents(
    $states: [ProductEventStateEnum!]
    $first: Int
    $after: String
    $sort: [ProductEventSortOption]
  ) {
    currentSession {
      id
      slug
      productEvents(states: $states, first: $first, after: $after, sort: $sort) {
        edges {
          node {
            ...ProductEventFields
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_MY_EVENTS = gql`
  query GetMyEvents(
    $states: [ProductEventStateEnum!]
    $first: Int
    $after: String
    $sort: [ProductEventSortOption]
    $includeOwnEvents: Boolean = false
  ) {
    myEvents(first: $first, sort: $sort, states: $states, after: $after, includeOwnEvents: $includeOwnEvents) {
      totalCount
      edges {
        node {
          id
          event {
            ...ProductEventFields
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_PRODUCT_EVENTS_FOR_ACCOUNT = gql`
  query GetProductEventsForAccount($id: UUID!, $first: Int) {
    account(id: $id) {
      id
      productEvents(first: $first, states: [SCHEDULED]) {
        edges {
          node {
            ...ProductEventFields
          }
        }
      }
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;

export const GET_REPLAYS_FOR_ACCOUNT = gql`
  query GetReplaysForAccount($id: UUID!, $first: Int, $after: String, $sort: [ProductEventSortOption]) {
    account(id: $id) {
      id
      productEvents(completed: WITH_REPLAYS, states: [COMPLETE], first: $first, after: $after, sort: $sort) {
        edges {
          node {
            ...ProductEventFields
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PRODUCT_EVENT_FIELDS}
`;
